import { Bell, Briefcase, CalendarDays, ClipboardList, LayoutDashboard, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { PushPromptModal } from '@/components/common';
import { ROUTES } from '@/config';
import { useUnreadCount } from '@/features/notifications';
import { useAuth, useWebSocketEvents } from '@/hooks';
import { cn } from '@/lib';
import { useNotificationsStore } from '@/stores';

const bottomNavItems = [
  { to: ROUTES.WORKER, icon: ClipboardList, labelKey: 'nav.tasks', end: true },
  { to: ROUTES.WORKER_JOBS, icon: Briefcase, labelKey: 'worker:nav.allJobs' },
  { to: ROUTES.WORKER_SCHEDULE, icon: CalendarDays, labelKey: 'worker:nav.schedule' },
  { to: ROUTES.WORKER_PROFILE, icon: User, labelKey: 'nav.profile' },
];

export function WorkerLayout() {
  const { t } = useTranslation(['common', 'worker']);
  const navigate = useNavigate();
  const { canAccessDashboard } = useAuth();
  useWebSocketEvents();
  useUnreadCount();
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  return (
    <div className="flex min-h-screen flex-col bg-worker-bg text-worker-text">
      <PushPromptModal />
      {/* Header */}
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-worker-border bg-worker-surface px-4">
        <div className="w-10" />
        <img src="/momcilo_logo.svg" alt="Momcilo" className="h-7" />
        <button
          type="button"
          onClick={() => navigate(ROUTES.WORKER_NOTIFICATIONS)}
          className="relative rounded-lg p-2 text-worker-text-secondary"
          aria-label={t('common:nav.notifications')}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 pb-20">
        <div className="mx-auto max-w-lg px-4 pt-4">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-20 flex h-16 items-center justify-around border-t border-worker-border bg-worker-surface safe-area-bottom">
        {bottomNavItems.map(({ to, icon: Icon, labelKey, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-1 px-4 py-1.5 text-xs font-medium transition-colors',
                isActive ? 'text-primary' : 'text-worker-text-secondary'
              )
            }
          >
            <Icon className="h-6 w-6" />
            <span>{t(labelKey)}</span>
          </NavLink>
        ))}
        {canAccessDashboard && (
          <NavLink
            to={ROUTES.ADMIN_TASKS}
            className="flex flex-col items-center gap-1 px-4 py-1.5 text-xs font-medium text-worker-text-secondary transition-colors"
          >
            <LayoutDashboard className="h-6 w-6" />
            <span>Dashboard</span>
          </NavLink>
        )}
      </nav>
    </div>
  );
}
